import {useState, useCallback, useEffect} from 'react';
import {View, StyleSheet, BackHandler} from 'react-native';
import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {useCurrentTheme} from '@/theming/useCurrentTheme';
import {AppDataMediaItem} from '@/data/app-data-interfaces';
import {ThemeType} from '@/theming/baseTheme';
import {useRouter} from 'expo-router';
import {appDataKeys} from '@/data/app-data-keys';
import {HeaderText} from '@/components/common/HeaderText';
import {MediaGrid} from '@/components/media/media-library/MediaGrid';
import {useLastViewedVideos} from "@/hooks/useLastViewedVideos";

interface LastViewedVideoScreenProps {
    orgId: string;
}

export const LastViewedVideoScreen = ({orgId}: LastViewedVideoScreenProps) => {
    const {t} = useTranslation();
    const router = useRouter();
    const {theme} = useCurrentTheme();
    const {videos: lastViewedVideos} = useLastViewedVideos(orgId);
    const queryClient = useQueryClient();

    const styles = createStyles(theme);

    const [isRefreshing, setIsRefreshing] = useState(false);

    const handleMediaPress = useCallback((item: AppDataMediaItem) => {
        // Navigate to video player
        router.push(`/organization/${orgId}/video/${item.id}?source=last-viewed`);
    }, [router, orgId]);

    const handleHeaderBackPress = useCallback(() => {
        router.back();
    }, [router]);

    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);
        try {
            await queryClient.invalidateQueries({queryKey: appDataKeys.all});
        } finally {
            setIsRefreshing(false);
        }
    }, [queryClient]);

    // Handle hardware back button
    useEffect(() => {
        const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
            router.back();
            return true;
        });
        return () => backHandler.remove();
    }, [router]);

    return (
        <View style={styles.container}>
            <HeaderText
                title={t('media.screens.lastViewed')}
                onBackPress={handleHeaderBackPress}
            />

            <MediaGrid
                mediaItems={lastViewedVideos}
                onMediaPress={handleMediaPress}
                isRefreshing={isRefreshing}
                onRefresh={handleRefresh}
                scrollEnabled={true}
                style={styles.contentContainer}
            />
        </View>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },

    contentContainer: {
        flex: 1,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.md
    },
});
